import React, { useCallback, useMemo, useState } from 'react'
import { FilePreview, FilePreviewMimeTypeIcon } from '@workwave-tidal/lab'
import { ActionBarItem } from '@workwave-tidal/core/components/ActionBar'
import MoreVert from '@mui/icons-material/MoreVert'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { IDocumentModel } from '@user/store/locationDocuments'
import { useAttachmentData } from '../hooks'
import Checkbox from '@mui/material/Checkbox'

import {
  AgreementAttachmentAcceptFileTypes,
  AttachmentMessages,
} from '../constants'
import { LightTooltip } from '@sales-center/components'

type Props = {
  attachment: IDocumentModel
  isSelectionEnable: boolean
  checked: boolean
  handleCheckChange: () => void
}

export function LocationAttachmentsListItem({
  attachment,
  isSelectionEnable,
  checked,
  handleCheckChange,
}: Props) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const { handleDownloadLocationAttachment, getMimeTypeFromFileName } =
    useAttachmentData()

  const { formatDate } = useLocalizationDateFormat()

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [setAnchorEl])

  const onDownloadClick = useCallback(async () => {
    handleDownloadLocationAttachment(
      attachment.documentId,
      attachment.fileName,
      attachment.isCompletedFormPdf,
    )
    handleClose()
  }, [handleDownloadLocationAttachment, handleClose, attachment])

  const disableAgreementButton = useMemo(() => {
    if (
      isSelectionEnable &&
      !AgreementAttachmentAcceptFileTypes.includes(
        getMimeTypeFromFileName(attachment.fileName),
      )
    ) {
      return true
    }
    return false
  }, [getMimeTypeFromFileName, attachment, isSelectionEnable])

  return (
    <Box
      sx={{
        mb: 0.8,
        display: 'flex',
      }}
    >
      {isSelectionEnable ? (
        <LightTooltip
          title={
            disableAgreementButton
              ? AttachmentMessages.InvalidAgreementAttachmentType
              : ''
          }
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Checkbox
              disabled={disableAgreementButton}
              checked={checked}
              onChange={handleCheckChange}
              sx={{
                height: 42,
                width: 42,
              }}
              data-test-id="location-attachment-checkbox"
            />
          </Box>
        </LightTooltip>
      ) : null}
      <Box sx={{ width: '100%' }}>
        <FilePreview
          icon={
            <FilePreviewMimeTypeIcon
              preset={getMimeTypeFromFileName(attachment.fileName)}
            />
          }
          title={attachment.fileName}
          tooltip=""
          subtitle={
            <Box
              color={'#0E283FB2'}
              display={'flex'}
              flexDirection={'row'}
              gap={0.8}
            >
              <Typography variant="body2">
                {formatDate(attachment.date)}
              </Typography>
            </Box>
          }
          variant="standard"
          actionsVisibility="always"
          actions={[
            <ActionBarItem
              onClick={(event) => setAnchorEl(event.currentTarget)}
              key="More"
              id="more-button"
              label="More"
              icon={<MoreVert />}
              data-test-id={`locationAttachmentMoreBtn-${attachment.documentId}`}
            />,
          ]}
        />
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <MenuItem
            onClick={onDownloadClick}
            data-test-id="locationAttachmentDownloadOption"
          >
            Download
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  )
}
